import React from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Target, HeartHandshake, Trophy, Users, ArrowRight } from "lucide-react";

const STORY_IMG = "https://images.unsplash.com/photo-1522071820081-009f0129c71c";

const VALUES = [
  { icon: Target, title: "Mission-first", body: "Every product decision starts with: does this move real money to real orgs faster?" },
  { icon: HeartHandshake, title: "Directors first", body: "We build for the executive director, the founder, the co-op treasurer — not for spectators." },
  { icon: Trophy, title: "Radical outcomes", body: "We measure success in dollars awarded, not vanity signups. The metric is on the wall." },
  { icon: Users, title: "Access without gatekeeping", body: "$100/mo. No enterprise sales calls. No 'contact us' pricing. The same tool for a solo LLC and a $10M nonprofit." },
];

export default function About() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-14 pb-16" data-testid="about-hero">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="overline mb-3">ABOUT GRANTFLOW</div>
            <h1 className="font-display text-5xl md:text-6xl font-extrabold tracking-tight leading-[1.05]">
              We built the tool we wished existed when we were chasing grants ourselves.
            </h1>
            <p className="mt-6 text-lg text-neutral-600 leading-relaxed max-w-2xl">
              GrantFlow started as a spreadsheet of federal deadlines, a folder of half-written Word docs, and one very
              tired executive director. We turned that mess into software so no organization has to lose funding to a
              paperwork bottleneck ever again.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/signup" className="btn-primary" data-testid="about-cta-signup">Start free <ArrowRight size={16} /></Link>
              <Link to="/contact" className="btn-ghost" data-testid="about-cta-contact">Talk to the team</Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative rounded-2xl overflow-hidden border border-neutral-200 aspect-[4/5] grain">
              <img src={STORY_IMG} alt="Team" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-neutral-200 bg-neutral-50 py-16" data-testid="about-values">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="overline mb-3">WHAT WE STAND FOR</div>
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-10">Four values, no committee.</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {VALUES.map((v, i) => {
              const Icon = v.icon;
              return (
                <div key={i} className="gf-card p-6" data-testid={`value-${i}`}>
                  <div className="h-11 w-11 rounded-lg bg-neutral-900 text-white flex items-center justify-center mb-4"><Icon size={18} /></div>
                  <h3 className="font-display text-lg font-semibold mb-2">{v.title}</h3>
                  <p className="text-sm text-neutral-600 leading-relaxed">{v.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-5 lg:px-8 py-20 text-center">
        <div className="overline mb-3">BY THE NUMBERS</div>
        <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-10">Grants awarded to US small business and nonprofits every year:</h2>
        <div className="grid grid-cols-3 gap-6">
          <div>
            <div className="font-display text-4xl md:text-5xl font-bold">$2.4B</div>
            <div className="text-xs uppercase tracking-widest text-neutral-500 mt-2">SBA + Federal</div>
          </div>
          <div>
            <div className="font-display text-4xl md:text-5xl font-bold">$76B</div>
            <div className="text-xs uppercase tracking-widest text-neutral-500 mt-2">Private foundations</div>
          </div>
          <div>
            <div className="font-display text-4xl md:text-5xl font-bold">63</div>
            <div className="text-xs uppercase tracking-widest text-neutral-500 mt-2">Live in GrantFlow</div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
